import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { DeviceInfo, DevicePlugin, JWebWindow } from '../types';
import { jWebStates } from '../Initializer';
import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';
import { timeout } from '../../../utils/withTimeout';
import { ClientOSError } from '../../../infra/errors/ClientOSError';
import { ClientOSErrorCodes } from '../../../infra/errors/ErrorCodes';

/**
 * Facade for Device plugin operations
 */
export class DevicePluginFacade {
  /**
   * Cached device info promise
   */
  private static cachedDeviceInfoPromise: Promise<DeviceInfo> | null = null;

  /**
   * Get the Device plugin
   */
  private static async getDevicePlugin(): Promise<DevicePlugin | undefined> {
    await this.isDevicePluginReady();

    const windowDevicePlugin: DevicePlugin = (window as JWebWindow)?.JWeb
      ?.Plugins?.Device;
    if (windowDevicePlugin === undefined) {
      internalLogger?.error(
        'Unable to obtain DevicePlugin (missing some JWeb plugin)'
      );
    }
    return windowDevicePlugin;
  }

  /**
   * A promise that resolves to true if the DevicePlugin is ready, otherwise false.
   */
  private static async isDevicePluginReady(): Promise<boolean> {
    // Is Jweb ready?
    await jWebStates.JWebLibrary;

    // Is the plugins registered?
    await jWebStates.defaultPluginsRegistered;

    // Was DevicePlugin installed?
    await jWebStates.plugins.Device;
    return true;
  }

  /**
   * Return information about the underlying device/os/platform
   */
  @loggerDecorator.debug.d3()
  @timeout({ message: 'JWeb: DevicePlugin is not ready' })
  public static async getDeviceInfo(): Promise<DeviceInfo> {
    const devicePlugin = await this.getDevicePlugin();

    // E.2: guard — DevicePlugin absent covers the same semantic as a getInfo() failure
    // from the caller's perspective. Both cases use TRD_005 (spec defines only one code
    // for DevicePluginFacade; ERR_010 was not in the Part 2 spec).
    if (!devicePlugin) {
      throw new ClientOSError(
        'JWeb error: DevicePlugin is not available',
        ClientOSErrorCodes.JWEB_DEVICE_INFO_ERROR,
        new Error('JWeb error: DevicePlugin is not available')
      );
    }

    return devicePlugin.getInfo().catch((error) => {
      internalLogger?.error('JWeb error: Error retrieving device info', error);
      if (error instanceof ClientOSError) throw error;
      throw new ClientOSError(
        'JWeb error: Error retrieving device info',
        ClientOSErrorCodes.JWEB_DEVICE_INFO_ERROR,
        error
      );
    });
  }

  /**
   * Return cached information about the underlying device/os/platform.
   * If the cache is empty, it fetches the device info and caches it.
   * If an error occurs, the cache is cleared so the next call will retry.
   */
  @loggerDecorator.debug.d3()
  public static async getDeviceInfoCached(): Promise<DeviceInfo> {
    if (this.cachedDeviceInfoPromise == null) {
      this.cachedDeviceInfoPromise = this.getDeviceInfo().catch((error) => {
        this.cachedDeviceInfoPromise = null;
        throw error;
      });
    }

    return this.cachedDeviceInfoPromise;
  }
}
